import React, { useEffect, useState } from "react";
import {
  Search,
  Filter,
  Calendar,
  User,
  Package,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import Header from "../../components/ui/Header";
import OrderDetailsModal from "./components/OrderDetailsModal";
import StatusUpdateModal from "./components/StatusUpdateModal";
import { useDispatch } from "react-redux";
import { getOrdersService } from "redux/services/orders_service";

const OrdersManagementInterface = () => {
  const dispatch = useDispatch();

  const [language, setLanguage] = useState("en");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const labels = {
    title: { en: "Orders Management", ar: "إدارة الطلبات" },
    search: { en: "Search orders...", ar: "البحث في الطلبات..." },
    filterStatus: { en: "Filter by Status", ar: "تصفية حسب الحالة" },
    all: { en: "All", ar: "الكل" },
    pending: { en: "Pending", ar: "قيد الانتظار" },
    inProgress: { en: "In Progress", ar: "قيد التنفيذ" },
    completed: { en: "Completed", ar: "مكتمل" },
    cancelled: { en: "Cancelled", ar: "ملغي" },
    orderNumber: { en: "Order #", ar: "طلب رقم" },
    customer: { en: "Customer", ar: "العميل" },
    products: { en: "Products", ar: "المنتجات" },
    status: { en: "Status", ar: "الحالة" },
    orderDate: { en: "Order Date", ar: "تاريخ الطلب" },
    actions: { en: "Actions", ar: "الإجراءات" },
    viewDetails: { en: "View Details", ar: "عرض التفاصيل" },
    updateStatus: { en: "Update Status", ar: "تحديث الحالة" },
    noOrders: { en: "No orders found", ar: "لم يتم العثور على طلبات" },
  };

  // Mock orders data
  const [orders, setOrders] = useState([
    {
      id: "ORD-001",
      customer: { id: "user1", name: "Ahmed Ali", phone: "+966501234567" },
      products: [
        { id: "prod1", name: "Engine Oil", quantity: 2, price: 150 },
        { id: "prod2", name: "Air Filter", quantity: 1, price: 80 },
      ],
      status: "Pending",
      orderDate: "2026-01-10T10:30:00",
      notes: "Customer requested delivery before 5 PM",
      timeline: [
        {
          status: "Pending",
          timestamp: "2026-01-10T10:30:00",
          admin: "System",
        },
      ],
    },
    {
      id: "ORD-002",
      customer: { id: "user2", name: "Sara Mohammed", phone: "+966509876543" },
      products: [{ id: "prod3", name: "Brake Pads", quantity: 4, price: 200 }],
      status: "In Progress",
      orderDate: "2026-01-09T14:20:00",
      notes: "Priority order",
      timeline: [
        {
          status: "Pending",
          timestamp: "2026-01-09T14:20:00",
          admin: "System",
        },
        {
          status: "In Progress",
          timestamp: "2026-01-09T15:00:00",
          admin: "Admin User",
        },
      ],
    },
    {
      id: "ORD-003",
      customer: { id: "user3", name: "Khalid Hassan", phone: "+966551122334" },
      products: [
        { id: "prod4", name: "Tire Set", quantity: 4, price: 800 },
        { id: "prod5", name: "Wheel Alignment", quantity: 1, price: 150 },
      ],
      status: "Completed",
      orderDate: "2026-01-08T09:15:00",
      notes: "",
      timeline: [
        {
          status: "Pending",
          timestamp: "2026-01-08T09:15:00",
          admin: "System",
        },
        {
          status: "In Progress",
          timestamp: "2026-01-08T10:00:00",
          admin: "Admin User",
        },
        {
          status: "Completed",
          timestamp: "2026-01-08T16:30:00",
          admin: "Admin User",
        },
      ],
    },
  ]);

  const getStatusColor = (status) => {
    const colors = {
      Pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      "In Progress": "bg-blue-500/20 text-blue-400 border-blue-500/30",
      Completed: "bg-green-500/20 text-green-400 border-green-500/30",
      Cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return (
      colors?.[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30"
    );
  };

  const getStatusIcon = (status) => {
    const icons = {
      Pending: <Clock className="w-4 h-4" />,
      "In Progress": <AlertCircle className="w-4 h-4" />,
      Completed: <CheckCircle className="w-4 h-4" />,
      Cancelled: <XCircle className="w-4 h-4" />,
    };
    return icons?.[status] || <Clock className="w-4 h-4" />;
  };

  const filteredOrders = orders?.filter((order) => {
    const matchesSearch =
      order?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      order?.customer?.name
        ?.toLowerCase()
        ?.includes(searchTerm?.toLowerCase()) ||
      order?.products?.some((p) =>
        p?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      );
    const matchesStatus =
      statusFilter === "all" || order?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setShowStatusModal(true);
  };

  const handleStatusUpdate = (orderId, newStatus, note) => {
    setOrders((prevOrders) =>
      prevOrders?.map((order) => {
        if (order?.id === orderId) {
          return {
            ...order,
            status: newStatus,
            notes: note || order?.notes,
            timeline: [
              ...order?.timeline,
              {
                status: newStatus,
                timestamp: new Date()?.toISOString(),
                admin: "Current Admin",
              },
            ],
          };
        }
        return order;
      }),
    );
    setShowStatusModal(false);
  };

  const calculateTotal = (products) => {
    return (
      products?.reduce(
        (sum, p) => sum + (p?.price || 0) * (p?.quantity || 0),
        0,
      ) || 0
    );
  };

  useEffect(() => {
    dispatch(getOrdersService({ page: 1 }));
  }, []);

  return (
    <div
      className={`min-h-screen bg-black ${language === "ar" ? "rtl" : "ltr"}`}
    >
      <Header language={language} setLanguage={setLanguage} />
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {labels?.title?.[language]}
          </h1>
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-900 border border-[#FFD700]/20 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={labels?.search?.[language]}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-black border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700]"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-black border border-[#FFD700]/30 rounded-lg text-white focus:outline-none focus:border-[#FFD700]"
              >
                <option value="all">{labels?.all?.[language]}</option>
                <option value="Pending">{labels?.pending?.[language]}</option>
                <option value="In Progress">
                  {labels?.inProgress?.[language]}
                </option>
                <option value="Completed">
                  {labels?.completed?.[language]}
                </option>
                <option value="Cancelled">
                  {labels?.cancelled?.[language]}
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders?.length === 0 ? (
            <div className="bg-gray-900 border border-[#FFD700]/20 rounded-lg p-12 text-center">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {labels?.noOrders?.[language]}
              </p>
            </div>
          ) : (
            filteredOrders?.map((order) => (
              <div
                key={order?.id}
                className="bg-gray-900 border border-[#FFD700]/20 rounded-lg p-6 hover:border-[#FFD700]/40 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Order Info */}
                  <div className="flex-1 space-y-3">
                    {/* Order Number & Status */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-xl font-bold text-[#FFD700]">
                        {labels?.orderNumber?.[language]} {order?.id}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-2 ${getStatusColor(order?.status)}`}
                      >
                        {getStatusIcon(order?.status)}
                        {labels?.[
                          order?.status?.toLowerCase()?.replace(" ", "")
                        ]?.[language] || order?.status}
                      </span>
                    </div>

                    {/* Customer Info */}
                    <div className="flex items-center gap-2 text-gray-300">
                      <User className="w-4 h-4 text-[#FFD700]" />
                      <span>{order?.customer?.name}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">
                        {order?.customer?.phone}
                      </span>
                    </div>

                    {/* Products */}
                    <div className="flex items-start gap-2 text-gray-300">
                      <Package className="w-4 h-4 text-[#FFD700] mt-1" />
                      <div>
                        <span className="font-medium">
                          {labels?.products?.[language]}:{" "}
                        </span>
                        {order?.products?.map((p, idx) => (
                          <span key={idx}>
                            {p?.name} (x{p?.quantity})
                            {idx < order?.products?.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Order Date & Total */}
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {new Date(order.orderDate)?.toLocaleDateString(
                            language === "ar" ? "ar-SA" : "en-US",
                          )}
                        </span>
                      </div>
                      <span className="text-[#FFD700] font-bold">
                        {calculateTotal(order?.products)}{" "}
                        {language === "ar" ? "ريال" : "SAR"}
                      </span>
                    </div>

                    {/* Notes Preview */}
                    {order?.notes && (
                      <div className="flex items-start gap-2 text-sm text-gray-400">
                        <FileText className="w-4 h-4 mt-0.5" />
                        <span className="line-clamp-1">{order?.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex lg:flex-col gap-2">
                    <button
                      onClick={() => handleViewDetails(order)}
                      className="flex-1 lg:flex-none px-4 py-2 bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30 rounded-lg hover:bg-[#FFD700]/20 transition-colors text-sm font-medium"
                    >
                      {labels?.viewDetails?.[language]}
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(order)}
                      className="flex-1 lg:flex-none px-4 py-2 bg-[#FFD700] text-black rounded-lg hover:bg-[#FFD700]/90 transition-colors text-sm font-bold"
                    >
                      {labels?.updateStatus?.[language]}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Modals */}
      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          language={language}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
      {showStatusModal && selectedOrder && (
        <StatusUpdateModal
          order={selectedOrder}
          language={language}
          onClose={() => setShowStatusModal(false)}
          onUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default OrdersManagementInterface;
